import { isRestHour, isRestDay } from '@spa/constants/RestTypes'
import { displayTwoNumber } from '@spa/services/utils'
import Immutable from 'immutable'
import Moment from 'moment'
import { extendMoment } from 'moment-range'

// @ts-ignore migration from js(x) to ts(x)
const moment = extendMoment(Moment)
export const roundHalf = number => Math.round(number * 2) / 2

export const getStartDate = rest => new Date(rest.starts_at)
export const getEndDate = rest => new Date(rest.ends_at)

export const getDuration = rest => {
  const startDate = getStartDate(rest)
  const endDate = getEndDate(rest)

  const timeDiffMillisecond = Math.abs(endDate.getTime() - startDate.getTime())
  return timeDiffMillisecond / (1000 * 60)
}

export const getRestStartMinutesFromMidnight = rest => {
  const startDate = getStartDate(rest)
  return startDate.getHours() * 60 + startDate.getMinutes()
}

export const getDayDuration = rest => {
  const timeDiffMinute = getDuration(rest)
  const diffDays = roundHalf(timeDiffMinute / (60 * 24))

  return diffDays
}

export const getRestEndMinutesFromMidnight = rest => {
  const startDate = getStartDate(rest)
  const endDate = getEndDate(rest)

  if (isRestDay(rest)) {
    const diffDays = getDayDuration(rest)

    return (
      Math.floor(diffDays) * 24 * 60 +
      (endDate.getHours() * 60 + endDate.getMinutes())
    )
  }

  const shiftEndDayAfter = startDate.getDate() !== endDate.getDate()
  return (
    (shiftEndDayAfter ? 24 * 60 : 0) +
    (endDate.getHours() * 60 + endDate.getMinutes())
  )
}

export const getRestStartWeek = rest => moment(rest.starts_at).week()
export const getRestEndWeek = rest => moment(rest.ends_at).week()

export const getCustomRestDuration = (rest, week) => {
  return rest.custom_value?.find(restValue => restValue.get(week))?.get(week, 0)
}

export const getRestDateISO = rest => {
  const startDate = new Date(rest.starts_at)
  const year = startDate.getFullYear()
  const month = startDate.getMonth() + 1
  const day = startDate.getDate()

  return `${year}-${displayTwoNumber(month)}-${displayTwoNumber(day)}`
}

export const getStartDateISO = rest => {
  const startDate = getStartDate(rest)
  const year = startDate.getFullYear()
  const month = startDate.getMonth() + 1
  const day = startDate.getDate()

  return `${year}-${displayTwoNumber(month)}-${displayTwoNumber(day)}`
}

export const getEndDateISO = rest => {
  const endDate = getEndDate(rest)
  const year = endDate.getFullYear()
  const month = endDate.getMonth() + 1
  const day = endDate.getDate()

  return `${year}-${displayTwoNumber(month)}-${displayTwoNumber(day)}`
}

// TODO: This generates the new dates using the timezone of the rest start date.
// If we change timezone (hey DST), the dates will be wrong.
// One way to do this would be to pass the timezone name we want the weeks in.
// (ex. "Europe/Paris"). This would move this function outside of the scope of what the rest
// module should do and therefore this should be in its own service.
export const getSlicedRestsPerWeek = rest => {
  const startDate = moment.parseZone(rest.starts_at)
  const endDate = moment.parseZone(rest.ends_at)
  const isSameTimeZone = startDate.utcOffset() === endDate.utcOffset()
  // Generate an array with every week the rest overlaps with.
  const startWeek = isSameTimeZone
    ? moment.parseZone(rest.starts_at).startOf('isoWeek')
    : moment(rest.starts_at).startOf('isoWeek')

  const endWeek = isSameTimeZone
    ? moment.parseZone(rest.ends_at).startOf('isoWeek')
    : moment(rest.ends_at).startOf('isoWeek')

  const weeks = Immutable.List(
    moment.range(startWeek, endWeek).by('day', { step: 7 })
  )
  // Split the rest per week.
  const splittedRests = weeks.reduce((result, week, index) => {
    // For the first week, use the rest actual start date.
    const start = index === 0 ? startDate : moment(week).format()
    // For the last week, use the rest actual end date.
    const end =
      index === weeks.size - 1 ? endDate : moment(week).add(1, 'week').format()

    // Return the same rest, but with the dates changed.
    // @ts-ignore migration from js(x) to ts(x)
    return result.set(
      // @ts-ignore migration from js(x) to ts(x)
      week.format('GGGG-[W]WW'),
      rest.merge({ starts_at: start, ends_at: end })
    )
  }, Immutable.Map())

  return splittedRests
}

export const getSlicedRestsPerDay = rest => {
  const startDate = moment(rest.starts_at)
  const endDate = moment(rest.ends_at)

  // Don't bother splitting the hour rests as they are mostly on one day.
  if (isRestHour(rest)) {
    return Immutable.Map({
      [moment(rest.starts_at).format('YYYY-MM-DD')]: rest
    })
  }

  const halfDays = Immutable.List(
    moment.range(startDate, endDate).by('hours', { step: 12 })
  )

  // Start by splitting the rests in half days.
  const splittedRestsInHalfDays = halfDays
    // @ts-ignore migration from js(x) to ts(x)
    .reduce((result, halfDay, index) => {
      // Skip the last iteration.
      if (index === halfDays.size - 1) {
        return result
      }

      // Compute the rest dates using the current iteration and the next one.
      const start = moment(halfDay).format()
      // @ts-ignore migration from js(x) to ts(x)
      const nextHalf = halfDays.get(index + 1)
      const end = moment(nextHalf).format()

      // Store the half day rest.
      // @ts-ignore migration from js(x) to ts(x)
      return result.push(rest.merge({ starts_at: start, ends_at: end }))
    }, Immutable.List())
    // @ts-ignore migration from js(x) to ts(x)
    .groupBy(rest => moment(rest.starts_at).format('YYYY-MM-DD'))

  // For each day, try to merge the rests if there are more than one.
  const splittedRests = splittedRestsInHalfDays.reduce((result, rests, day) => {
    let mergedRest
    // @ts-ignore migration from js(x) to ts(x)
    if (rests.size === 1) {
      // @ts-ignore migration from js(x) to ts(x)
      mergedRest = rests.first()
    } else {
      // @ts-ignore migration from js(x) to ts(x)
      mergedRest = rests.first().merge({ ends_at: rests.last().ends_at })
    }
    // @ts-ignore migration from js(x) to ts(x)
    return result.set(day, mergedRest)
  }, Immutable.Map())

  return splittedRests
}
